import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CaretRightIcon } from '@phosphor-icons/react';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from '@/components/ui-kit/sidebar';
import { Collapsible, CollapsibleContent } from '@/components/ui-kit/collapsible';
import { SidebarMenuItemProps } from '@/models/sidebar';
import { MenuIcon, MenuIconName, SIDEBAR_NAV_ICON_SIZE } from '@/components/core';
import { Badge } from '@/components/ui-kit/badge';
import { useIsProtected } from '@/state/store/auth/use-is-protected';
import { cn } from '@/lib/utils';

const navButtonClassName = (isActive: boolean) =>
  cn(
    'relative h-auto min-h-10 gap-2.5 overflow-hidden rounded-md px-2.5 py-2 font-medium',
    'transition-colors duration-200',
    isActive
      ? [
          '!bg-primary-50 !text-primary-600',
          'hover:!bg-primary-50 hover:!text-primary-600 active:!bg-primary-50',
          'data-[active=true]:!bg-primary-50 data-[active=true]:!text-primary-600',
          'data-[active=true]:hover:!bg-primary-50 data-[active=true]:hover:!text-primary-600',
        ]
      : [
          'bg-transparent text-neutral-700',
          'hover:bg-neutral-100 hover:text-neutral-700',
          'dark:hover:bg-white/[0.06]',
        ]
  );

export const SidebarMenuItemComponent = ({
  item,
  showText,
  onClick,
}: Readonly<SidebarMenuItemProps>) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const [isOpen, setIsOpen] = useState(false);

  const matchesPath = (path: string) => pathname === path || pathname.startsWith(`${path}/`);

  const isParentActive = hasChildren && item.children?.some((child) => matchesPath(child.path));
  const isActive = matchesPath(item.path) || isParentActive;

  // When a child's path is a prefix of a sibling's (e.g. "/contacts" and
  // "/contacts/custom-fields"), both would match via matchesPath. Only the
  // child with the longest (most specific) matching path should highlight.
  const activeChildId = hasChildren
    ? item.children
        ?.filter((child) => matchesPath(child.path))
        .sort((a, b) => b.path.length - a.path.length)[0]?.id
    : undefined;

  // The parent row itself navigates (like Mailchimp's "Audience"), so the
  // sub-menu simply follows whichever route is active — expanded whenever
  // the parent or one of its children is the current page, collapsed
  // otherwise. No separate manual expand/collapse control is needed.
  useEffect(() => {
    setIsOpen(isActive ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isActive]);

  const strokeWidth = 2.2;

  const itemRequiresAdmin =
    item.roles &&
    (Array.isArray(item.roles) ? item.roles.includes('admin') : item.roles === 'admin');

  const { isProtected: userHasAdminRole } = useIsProtected({ roles: ['admin'] });

  const shouldShowAdminBadge = itemRequiresAdmin && !userHasAdminRole;

  const renderIcon = (iconName: MenuIconName | undefined) => {
    if (!iconName) return null;

    return (
      <div className="flex size-[18px] shrink-0 items-center justify-center">
        <MenuIcon
          name={iconName}
          size={SIDEBAR_NAV_ICON_SIZE}
          strokeWidth={strokeWidth}
          weight={isActive ? 'fill' : 'regular'}
          className={
            isActive
              ? 'text-primary-600'
              : 'text-neutral-700 group-hover/menu-item:text-neutral-700'
          }
        />
      </div>
    );
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const labelClassName = cn(
    'nav-label truncate text-sm',
    !showText && 'hidden',
    'group-data-[collapsible=icon]:hidden',
    isActive ? 'text-primary-600' : 'text-neutral-700'
  );

  if (hasChildren) {
    return (
      <Collapsible className="group/collapsible" open={isOpen} onOpenChange={setIsOpen}>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            isActive={isActive}
            tooltip={t(item.name)}
            className={navButtonClassName(isActive)}
          >
            <Link
              to={item.path}
              className="flex w-full items-center"
              onClick={handleClick}
              aria-label={t(item.name)}
              title={t(item.name)}
              aria-current={isActive ? 'page' : undefined}
            >
              {renderIcon(item.icon as MenuIconName)}
              <span className={labelClassName}>{t(item.name)}</span>
              {shouldShowAdminBadge && showText && (
                <Badge
                  variant="outline"
                  className="ml-2 h-4 border-primary bg-primary/10 px-1.5 py-0 text-[10px] font-semibold text-primary hover:bg-primary/10"
                >
                  {t('ADMIN')}
                </Badge>
              )}
              {showText && (
                <CaretRightIcon
                  strokeWidth={strokeWidth}
                  className={cn(
                    'ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-90',
                    'group-data-[collapsible=icon]:hidden',
                    isActive ? 'text-primary-600' : 'text-medium-emphasis'
                  )}
                />
              )}
            </Link>
          </SidebarMenuButton>

          <CollapsibleContent>
            {/* mx-0/border-0/px-0 strip the default sub-list indent (margin +
                connector border + padding) so the only thing offsetting the
                child text is the same icon-slot width + margin used by the
                parent row below, keeping both labels flush with each other. */}
            <SidebarMenuSub className="mx-0 border-0 px-0">
              {item.children?.map((child) => {
                const isChildActive = child.id === activeChildId;
                return (
                  <SidebarMenuSubItem key={child.id}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={isChildActive}
                      className={cn(
                        'relative h-9 translate-x-0 overflow-hidden rounded-md px-3 font-medium transition-colors duration-200',
                        isChildActive
                          ? 'bg-primary-50 text-primary-600 hover:bg-primary-50 hover:text-primary-600 data-[active=true]:bg-primary-50 data-[active=true]:text-primary-600'
                          : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                      )}
                    >
                      <Link
                        to={child.path}
                        className="flex w-full items-center"
                        onClick={handleClick}
                        aria-current={isChildActive ? 'page' : undefined}
                      >
                        <div className="size-[18px] shrink-0" aria-hidden="true" />
                        <span
                          className={cn(
                            'truncate text-sm',
                            !showText && 'hidden',
                            isChildActive ? 'text-primary-600' : 'text-high-emphasis'
                          )}
                        >
                          {t(child.name)}
                        </span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={t(item.name)}
        className={navButtonClassName(isActive)}
      >
        <Link
          to={item.path}
          className="flex w-full items-center"
          onClick={handleClick}
          aria-label={t(item.name)}
          title={t(item.name)}
          aria-current={isActive ? 'page' : undefined}
        >
          {renderIcon(item.icon as MenuIconName)}
          <span className={labelClassName}>{t(item.name)}</span>
          {shouldShowAdminBadge && showText && (
            <Badge
              variant="outline"
              className="ml-auto h-4 border-primary bg-primary/10 px-1.5 py-0 text-[10px] font-semibold text-primary hover:bg-primary/10"
            >
              {t('ADMIN')}
            </Badge>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
